import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bell } from 'lucide-react';
import { offboardingAPI, applicationsAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';

interface NotificationBadgeProps {
  inline?: boolean;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({ inline = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.email) {
      loadPendingCount();
    }
  }, [user]);

  const loadPendingCount = async () => {
    if (!user?.email) return;

    const userEmail = user.email;

    try {
      setLoading(true);

      // Get all applications where I'm an admin
      const allApps = await applicationsAPI.getAll();
      const myApps = allApps.filter((app) => app.admins.includes(userEmail));

      if (myApps.length === 0) {
        setPendingCount(0);
        return;
      }

      // Get all offboarding requests
      const allOffboardings = await offboardingAPI.getAll();

      // Count pending confirmations for my apps
      let pending = 0;
      allOffboardings.forEach((offboarding) => {
        // Skip malformed offboardings
        if (!offboarding || !Array.isArray(offboarding.applications)) {
          return;
        }

        if (offboarding.status !== 'completed') {
          offboarding.applications.forEach((app) => {
            if (app.adminEmails && app.adminEmails.includes(userEmail)) {
              const myStatus = app.confirmationStatus?.[userEmail];
              if (!myStatus || myStatus.status === 'pending') {
                pending++;
              }
            }
          });
        }
      });

      setPendingCount(pending);
    } catch (error) {
      console.error('Error loading pending count:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || pendingCount === 0) {
    return null;
  }

  // Inline mode for sidebar nav items
  if (inline) {
    return (
      <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold">
        {pendingCount > 9 ? '9+' : pendingCount}
      </span>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => navigate('/my-tasks')}
      className="relative gap-2 hover:bg-accent"
    >
      <Bell className="w-4 h-4" />
      {pendingCount > 0 && (
        <Badge
          variant="destructive"
          className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
        >
          {pendingCount > 9 ? '9+' : pendingCount}
        </Badge>
      )}
      <span className="hidden sm:inline">Tasks</span>
    </Button>
  );
};
